/**
 * GDPR API Service
 * 
 * API functions for GDPR compliance features:
 * - Data export (Article 20)
 * - Data deletion (Article 17)
 * - Privacy policy
 * - Terms of service
 */

import { apiClient } from './client';

export interface DataExportRequest {
  id: string;
  user_id: string;
  organization_id?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  format: 'json' | 'csv';
  requested_at: string;
  completed_at?: string;
  download_url?: string;
  expires_at?: string;
  error_message?: string;
}

export interface DataDeletionRequest {
  id: string;
  user_id: string;
  organization_id?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  reason?: string;
  requested_at: string;
  completed_at?: string;
  verified_at?: string;
  error_message?: string;
}

export interface PrivacyPolicy {
  version: string;
  last_updated: string;
  policy: {
    title: string;
    description: string;
    data_controller: {
      name: string;
      contact: string;
    };
    data_we_collect: string[];
    legal_basis: string[];
    data_retention: Record<string, string>;
    your_rights: string[];
    how_to_exercise_rights: string;
  };
}

export interface ExportResponse {
  success: boolean;
  request_id: string;
  status: string;
  download_url?: string;
  expires_at?: string;
}

export interface DeletionResponse {
  success: boolean;
  request_id: string;
  status: string;
  message: string;
  verification_required: boolean;
  verification_code?: string;
}

export interface VerifyDeletionResponse {
  success: boolean;
  request_id: string;
  status: string;
  message: string;
}

// Get privacy policy
export async function getPrivacyPolicy(): Promise<PrivacyPolicy> {
  return apiClient.get<PrivacyPolicy>('/gdpr/privacy-policy');
}

// Request data export
export async function requestDataExport(
  format: 'json' | 'csv' = 'json',
  organizationId?: string
): Promise<ExportResponse> {
  return apiClient.post<ExportResponse>('/gdpr/export', {
    format,
    organization_id: organizationId,
  });
}

// Download exported data
export async function downloadDataExport(requestId: string): Promise<Blob> {
  const response = await fetch(
    `${apiClient['baseUrl']}/gdpr/export/${requestId}/download`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Download failed: ${response.statusText}`);
  }

  return response.blob();
}

// Request data deletion
export async function requestDataDeletion(reason?: string): Promise<DeletionResponse> {
  return apiClient.post<DeletionResponse>('/gdpr/delete', { reason });
}

// Verify and confirm data deletion
export async function verifyDataDeletion(
  requestId: string,
  code: string
): Promise<VerifyDeletionResponse> {
  return apiClient.post<VerifyDeletionResponse>(`/gdpr/delete/${requestId}/verify`, {
    code,
  });
}

// Get GDPR audit log
export async function getGdprAuditLog(limit: number = 100): Promise<{
  logs: Array<{
    action: string;
    resource_type: string;
    resource_id: string;
    details: string;
    performed_at: string;
  }>;
  count: number;
}> {
  return apiClient.get(`/gdpr/audit-log?limit=${limit}`);
}

// Export class for dependency injection
export class GdprService {
  async getPrivacyPolicy(): Promise<PrivacyPolicy> {
    return getPrivacyPolicy();
  }

  async requestDataExport(
    format: 'json' | 'csv' = 'json',
    organizationId?: string
  ): Promise<ExportResponse> {
    return requestDataExport(format, organizationId);
  }

  async downloadDataExport(requestId: string): Promise<Blob> {
    return downloadDataExport(requestId);
  }

  async requestDataDeletion(reason?: string): Promise<DeletionResponse> {
    return requestDataDeletion(reason);
  }

  async verifyDataDeletion(
    requestId: string,
    code: string
  ): Promise<VerifyDeletionResponse> {
    return verifyDataDeletion(requestId, code);
  }

  async getAuditLog(limit: number = 100): Promise<{
    logs: Array<{
      action: string;
      resource_type: string;
      resource_id: string;
      details: string;
      performed_at: string;
    }>;
    count: number;
  }> {
    return getGdprAuditLog(limit);
  }
}

export const gdprService = new GdprService();
